import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { API_ERROR_CODES } from '@mcikm-vote/shared';
import type {
  Abstimmung,
  AbstimmungEingabe,
  Beteiligung,
  Ergebnis,
  TokenUebersicht,
} from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';
import { DURCHFUEHRUNG_SCHLUESSEL } from './durchfuehrungApi';
import { VERANSTALTUNGEN_SCHLUESSEL } from './veranstaltungenApi';

/**
 * Anbindung der Abstimmungsverwaltung (§10, §15, §17, §18).
 *
 * **Es gibt hier keinen Weg zu einer Verteilung vor dem Schließen.** {@link useBeteiligung} liefert
 * ausschließlich die Zahlen aus §15, {@link useErgebnis} verweigert der Server für eine geöffnete
 * Abstimmung (§16). Beides ist serverseitig durchgesetzt; dieses Modul bildet es nur ab.
 */

const VERANSTALTUNGEN = '/api/v1/admin/veranstaltungen';
const ABSTIMMUNGEN = '/api/v1/admin/abstimmungen';

export const ABSTIMMUNGEN_SCHLUESSEL = ['admin', 'abstimmungen'] as const;

function abstimmungSchluessel(id: string): readonly unknown[] {
  return [...ABSTIMMUNGEN_SCHLUESSEL, id];
}

/**
 * Abstand der Beteiligungsabfrage während einer geöffneten Abstimmung (§15).
 *
 * Serverseitig mit `@OhneAktivitaet()` gekennzeichnet: die Abfrage verlängert die
 * 30-Minuten-Frist der Administratorsitzung **nicht** (§5.2).
 */
const BETEILIGUNG_ABSTAND_MS = 3_000;

export function useAbstimmungen(veranstaltungId: string): UseQueryResult<Abstimmung[], ApiFehler> {
  return useQuery<Abstimmung[], ApiFehler>({
    queryKey: [...ABSTIMMUNGEN_SCHLUESSEL, { veranstaltungId }],
    queryFn: () => apiAnfrage<Abstimmung[]>(`${VERANSTALTUNGEN}/${veranstaltungId}/abstimmungen`),
    enabled: veranstaltungId !== '',
  });
}

export function useAbstimmung(id: string): UseQueryResult<Abstimmung, ApiFehler> {
  return useQuery<Abstimmung, ApiFehler>({
    queryKey: abstimmungSchluessel(id),
    queryFn: () => apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}`),
    enabled: id !== '',
  });
}

/** Beteiligungszahlen (§15). Enthält niemals eine Verteilung (§16). */
export function useBeteiligung(
  id: string,
  laufend: boolean,
): UseQueryResult<Beteiligung, ApiFehler> {
  return useQuery<Beteiligung, ApiFehler>({
    queryKey: [...abstimmungSchluessel(id), 'beteiligung'],
    queryFn: () => apiAnfrage<Beteiligung>(`${ABSTIMMUNGEN}/${id}/beteiligung`),
    enabled: id !== '',
    ...(laufend ? { refetchInterval: BETEILIGUNG_ABSTAND_MS } : {}),
  });
}

/** Das aggregierte Ergebnis. Der Server liefert es nur für eine geschlossene Abstimmung (§16). */
export function useErgebnis(id: string, aktiv: boolean): UseQueryResult<Ergebnis, ApiFehler> {
  return useQuery<Ergebnis, ApiFehler>({
    queryKey: [...abstimmungSchluessel(id), 'ergebnis'],
    queryFn: () => apiAnfrage<Ergebnis>(`${ABSTIMMUNGEN}/${id}/ergebnis`),
    enabled: aktiv && id !== '',
    retry: false,
  });
}

function useAktualisierend<TDaten, TVariablen>(
  fn: (variablen: TVariablen) => Promise<TDaten>,
): UseMutationResult<TDaten, ApiFehler, TVariablen> {
  const queryClient = useQueryClient();

  return useMutation<TDaten, ApiFehler, TVariablen>({
    mutationFn: fn,
    onSettled: (_daten, fehler) => {
      // Bei Erfolg: Ein Statuswechsel wirkt sich auf Liste, Einzelansicht, Beteiligung und
      // Ergebnis aus — und über den Übergang „freigegeben → laufend" (§6.2) auch auf die
      // Veranstaltung.
      //
      // Bei `ZUSTAND_VERALTET` erst recht (§25.3): Der Serverstand ist offenkundig ein anderer als
      // der angezeigte. Die Meldung allein hülfe wenig, wenn die Seite danach weiter den alten
      // Stand zeigte — und ein zweiter Versuch scheiterte aus demselben Grund erneut.
      if (fehler === null || fehler.code === API_ERROR_CODES.ZUSTAND_VERALTET) {
        void queryClient.invalidateQueries({ queryKey: ABSTIMMUNGEN_SCHLUESSEL });
        void queryClient.invalidateQueries({ queryKey: VERANSTALTUNGEN_SCHLUESSEL });
        void queryClient.invalidateQueries({ queryKey: DURCHFUEHRUNG_SCHLUESSEL });
      }
    },
  });
}

export function useAbstimmungAnlegen(
  veranstaltungId: string,
): UseMutationResult<Abstimmung, ApiFehler, AbstimmungEingabe> {
  return useAktualisierend((eingabe) =>
    apiAnfrage<Abstimmung>(`${VERANSTALTUNGEN}/${veranstaltungId}/abstimmungen`, {
      methode: 'POST',
      koerper: eingabe,
    }),
  );
}

export function useAbstimmungBearbeiten(
  id: string,
): UseMutationResult<Abstimmung, ApiFehler, AbstimmungEingabe> {
  return useAktualisierend((eingabe) =>
    apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}`, { methode: 'PATCH', koerper: eingabe }),
  );
}

export function useAbstimmungLoeschen(): UseMutationResult<void, ApiFehler, string> {
  return useAktualisierend((id) =>
    apiAnfrage<void>(`${ABSTIMMUNGEN}/${id}`, { methode: 'DELETE' }),
  );
}

/** Dupliziert eine Abstimmung als Entwurf (§10.3). */
export function useAbstimmungDuplizieren(): UseMutationResult<Abstimmung, ApiFehler, string> {
  return useAktualisierend((id) =>
    apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/duplizieren`, { methode: 'POST' }),
  );
}

/** Verschiebt einen Entwurf in der Anzeigereihenfolge (§10.3). */
export function useAbstimmungVerschieben(): UseMutationResult<
  Abstimmung,
  ApiFehler,
  { id: string; richtung: 'hoch' | 'runter' }
> {
  return useAktualisierend(({ id, richtung }) =>
    apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/verschieben`, {
      methode: 'POST',
      koerper: { richtung },
    }),
  );
}

/** Setzt, ändert oder entfernt die Ergebnisfeststellung (§18.4, §18.5). */
export function useFeststellung(
  id: string,
): UseMutationResult<Abstimmung, ApiFehler, string | null> {
  return useAktualisierend((text) =>
    text === null
      ? apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/feststellung`, { methode: 'DELETE' })
      : apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/feststellung`, {
          methode: 'PUT',
          koerper: { text },
        }),
  );
}

/** Fügt einen unveränderlichen Berichtigungsvermerk an (§18.5). */
export function useBerichtigung(id: string): UseMutationResult<Abstimmung, ApiFehler, string> {
  return useAktualisierend((text) =>
    apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/berichtigungen`, {
      methode: 'POST',
      koerper: { text },
    }),
  );
}

/** Zustandsändernde Aktionen nach §10.3, §14.3, §17.3 und §18.3. */
export type AbstimmungAktion =
  | 'bereit'
  | 'entwurf'
  | 'oeffnen'
  | 'schliessen'
  | 'annullieren'
  | 'ergebnis/veroeffentlichen'
  | 'ergebnis/beenden';

/**
 * Eine zustandsverändernde Aktion an einer Abstimmung.
 *
 * `erwarteterStatus` ist der Status, den die Oberfläche **angezeigt** hat, als der Administrator
 * die Aktion auslöste. Weicht er vom Serverstand ab, wird die Aktion mit `ZUSTAND_VERALTET`
 * abgewiesen statt ausgeführt (§25.3). Das Feld ist optional, damit ältere Aufrufe gültig bleiben;
 * jede Stelle, die es senden kann, sollte es senden.
 */
export interface AbstimmungAktionAnfrage {
  aktion: AbstimmungAktion;
  grund?: string;
  erwarteterStatus?: string;
}

export function useAbstimmungAktion(
  id: string,
): UseMutationResult<Abstimmung, ApiFehler, AbstimmungAktionAnfrage> {
  return useAktualisierend(({ aktion, grund, erwarteterStatus }) => {
    const koerper = {
      ...(grund === undefined ? {} : { grund }),
      ...(erwarteterStatus === undefined ? {} : { erwarteterStatus }),
    };

    return apiAnfrage<Abstimmung>(`${ABSTIMMUNGEN}/${id}/${aktion}`, {
      methode: 'POST',
      ...(Object.keys(koerper).length === 0 ? {} : { koerper }),
    });
  });
}

/** Freigabe eines Ersatz-Tokens für die laufende Abstimmung (§7.8). */
export function useErsatzFreigeben(): UseMutationResult<TokenUebersicht, ApiFehler, string> {
  return useAktualisierend((tokenId) =>
    apiAnfrage<TokenUebersicht>(
      `/api/v1/admin/tokens/${tokenId}/fuer-laufende-abstimmung-freigeben`,
      { methode: 'POST' },
    ),
  );
}
